import React, { useEffect, useMemo, useState } from "react";
import {
    CircularProgress,
    Dialog,
    IconButton,
    Switch,
    TextField,
} from "@mui/material";
import { Check, Search, ShieldCheck, X } from "lucide-react";
import { useFormik } from "formik";
import * as yup from "yup";
import {
    createRoleAction,
    editRoleAction,
    getModuleListAction,
} from "../../Actions/RoleActions";
import { MyCloseIcon } from "../../Assets/svg/MyIcons";
import { FRONTEND_MESSAGES } from "../../utils/messages";
import "./AddEditRoles.css";

const { VALIDATION } = FRONTEND_MESSAGES;

const startsWithCan = (word) => word.toLowerCase().startsWith("can");

const addSpaceBeforeCapital = (str) => {
    let result = "";
    for (let i = 0; i < str.length; i++) {
        if (str[i] === str[i].toUpperCase() && i !== 0) {
            result += " ";
        }
        result += str[i];
    }
    return result;
};

// Permissions a module actually exposes (flagged true on the module record)
const getAvailableKeys = (module) =>
    Object.keys(module).filter(
        (key) => startsWithCan(key) && module[key] === true
    );

const buildEmptyEntry = (module) => {
    const entry = { moduleId: module.id, isAllChecked: false };
    Object.entries(module).forEach(([key, value]) => {
        if (startsWithCan(key) && typeof value === "boolean") {
            entry[key] = false;
        }
    });
    return entry;
};

const AddEditRolesModal = ({
    open,
    handleClose,
    roleDataById,
    setSnackbar,
    showViewRolesModal,
    fetchRoles,
}) => {
    const [buttonDisable, setButtonDisable] = useState(false);
    const [loading, setLoading] = useState(false);
    const [modulesLoading, setModulesLoading] = useState(true);
    const [modules, setModules] = useState([]);
    const [search, setSearch] = useState("");
    const [modulePermission, setModulePermission] = useState(
        roleDataById?.rolePermissions || []
    );
    const [isAdminChecked, setIsAdminChecked] = useState(
        roleDataById ? roleDataById?.isAdmin : false
    );

    const readOnly = Boolean(showViewRolesModal);

    const validationSchema = yup.object().shape({
        roleName: yup
            .string()
            .trim()
            .required(VALIDATION.REQUIRED_FIELD("Role name"))
            .min(1, VALIDATION.MIN_LENGTH("Role name", 1))
            .max(20, VALIDATION.MAX_LENGTH("Role name", 20))
            .matches(/^[a-zA-Z0-9\s_-]+$/, VALIDATION.INVALID_FORMAT("Role name")),
    });

    const formik = useFormik({
        initialValues: {
            roleName: roleDataById?.roleName || "",
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            setButtonDisable(true);
            setLoading(true);

            const data = {
                roleName: values.roleName,
                module: modulePermission,
                isAdmin: isAdminChecked,
            };

            try {
                const res =
                    roleDataById && roleDataById.id
                        ? await editRoleAction(roleDataById.id, data)
                        : await createRoleAction(data);

                setSnackbar({
                    open: true,
                    message: res.data.message,
                    severity: "success",
                });
                fetchRoles();
                formik.resetForm();
                handleClose();
            } catch (error) {
                setSnackbar({
                    open: true,
                    message: error.response?.data?.message || "Something went wrong",
                    severity: "error",
                });
            } finally {
                setLoading(false);
                setButtonDisable(false);
            }
        },
    });

    const handleRoleNameChange = (e) => {
        formik.setFieldValue(e.target.name, e.target.value.replace(/^\s+/, ""));
        formik.setFieldTouched(e.target.name, true, false);
    };

    useEffect(() => {
        const fetchModules = async () => {
            setModulesLoading(true);
            try {
                const res = await getModuleListAction();
                const { data } = res.data;
                setModules(Array.isArray(data) ? data : []);
            } catch (error) {
                setSnackbar({
                    open: true,
                    message:
                        error.response?.data?.message || "Failed to load modules",
                    severity: "error",
                });
            } finally {
                setModulesLoading(false);
            }
        };

        fetchModules();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Make sure every module has a permission entry to toggle against
    useEffect(() => {
        if (!modules.length) return;
        setModulePermission((prev) => {
            const missing = modules.filter(
                (module) => !prev.some((p) => p.moduleId === module.id)
            );
            if (!missing.length) return prev;
            return [...prev, ...missing.map(buildEmptyEntry)];
        });
    }, [modules]);

    const getEntry = (moduleId) =>
        modulePermission.find((entry) => entry.moduleId === moduleId);

    const isChecked = (moduleId, permissionName) =>
        Boolean(getEntry(moduleId)?.[permissionName]);

    const isModuleFullyChecked = (module) => {
        const entry = getEntry(module.id);
        if (!entry) return false;
        const availableKeys = getAvailableKeys(module);
        return (
            availableKeys.length > 0 &&
            availableKeys.every((key) => entry[key] === true)
        );
    };

    const countEnabled = (module) => {
        const entry = getEntry(module.id);
        if (!entry) return 0;
        return getAvailableKeys(module).filter((key) => entry[key] === true)
            .length;
    };

    // A module only stores the permissions it actually exposes, so every write
    // is scoped to that module's available keys.
    const setEntryKeys = (entry, keys, checked) => {
        const next = { ...entry };
        keys.forEach((key) => {
            next[key] = checked;
        });
        next.isAllChecked = keys.length > 0 && checked;
        return next;
    };

    const handleAllPermissionToggle = (moduleId, checked) => {
        const module = modules.find((item) => item.id === moduleId);
        if (!module) return;
        const availableKeys = getAvailableKeys(module);
        setModulePermission((prev) =>
            prev.map((entry) =>
                entry.moduleId === moduleId
                    ? setEntryKeys(entry, availableKeys, checked)
                    : entry
            )
        );
    };

    const handlePermissionToggle = (moduleId, permissionName) => {
        const module = modules.find((item) => item.id === moduleId);
        if (!module) return;
        const availableKeys = getAvailableKeys(module);
        setModulePermission((prev) =>
            prev.map((entry) => {
                if (entry.moduleId !== moduleId) return entry;
                const next = { ...entry, [permissionName]: !entry[permissionName] };
                next.isAllChecked = availableKeys.every(
                    (key) => next[key] === true
                );
                return next;
            })
        );
    };

    const handleAdministratorAccessPermissionToggle = (e) => {
        const checked = e.target.checked;
        setIsAdminChecked(checked);
        setModulePermission((prev) =>
            prev.map((entry) => {
                const module = modules.find((item) => item.id === entry.moduleId);
                return setEntryKeys(
                    entry,
                    module ? getAvailableKeys(module) : [],
                    checked
                );
            })
        );
    };

    const visibleModules = useMemo(() => {
        const term = search.trim().toLowerCase();
        if (!term) return modules;
        return modules.filter((module) =>
            module.moduleName?.toLowerCase().includes(term)
        );
    }, [modules, search]);

    const enabledModuleCount = modules.filter(
        (module) => countEnabled(module) > 0
    ).length;

    const title = readOnly ? "View Role" : roleDataById ? "Update Role" : "Add Role";
    const subtitle = readOnly
        ? "Review the access this role grants across modules."
        : "Name the role, then pick what it can do in each module.";

    const renderChip = ({ key, label, checked, onChange, isAll }) => (
        <label
            key={key}
            className={`rp-chip${isAll ? " is-all" : ""}${checked ? " is-on" : ""}${readOnly ? " is-locked" : ""
                }`}
        >
            <input
                type="checkbox"
                checked={checked}
                onChange={onChange}
                disabled={readOnly}
            />
            <span className="rp-chip-box">
                <Check size={11} strokeWidth={3} aria-hidden />
            </span>
            {label}
        </label>
    );

    return (
        <Dialog
            open={open}
            onClose={handleClose}
            fullWidth
            maxWidth="md"
            PaperProps={{ className: "rp-dialog" }}
        >
            <div className="rp-header">
                <span className="rp-header-icon" aria-hidden>
                    <ShieldCheck size={20} />
                </span>
                <div className="rp-header-copy">
                    <h2>{title}</h2>
                    <p>{subtitle}</p>
                </div>
                <IconButton
                    aria-label="close"
                    className="rp-close"
                    onClick={handleClose}
                >
                    <MyCloseIcon />
                </IconButton>
            </div>

            <form onSubmit={formik.handleSubmit} className="rp-form">
                <div className="rp-body">
                    <section className="rp-section">
                        <div className="rp-section-head">
                            <h3>Role details</h3>
                        </div>
                        <div className="rp-card">
                            <div className="rp-details">
                                <div className="rp-field">
                                    <label htmlFor="roleName">
                                        Role name<span className="rp-req">*</span>
                                    </label>
                                    <TextField
                                        id="roleName"
                                        placeholder="Enter role name"
                                        name="roleName"
                                        size="small"
                                        fullWidth
                                        value={formik.values.roleName}
                                        onChange={handleRoleNameChange}
                                        onBlur={formik.handleBlur}
                                        disabled={readOnly}
                                        error={
                                            formik.touched.roleName &&
                                            Boolean(formik.errors.roleName)
                                        }
                                        helperText={
                                            formik.touched.roleName &&
                                            formik.errors.roleName
                                        }
                                    />
                                </div>

                                <div className="rp-admin-slot">
                                    <label
                                        className={`rp-admin-card${
                                            isAdminChecked ? " is-on" : ""
                                        }${readOnly ? " is-locked" : ""}`}
                                    >
                                        <span>
                                            <p className="rp-admin-title">
                                                Administrator access
                                            </p>
                                            <p className="rp-admin-sub">
                                                Turns on every permission for
                                                every module.
                                            </p>
                                        </span>
                                        <Switch
                                            checked={isAdminChecked}
                                            onChange={
                                                handleAdministratorAccessPermissionToggle
                                            }
                                            disabled={readOnly}
                                        />
                                    </label>
                                </div>
                            </div>
                        </div>
                    </section>

                    <section className="rp-section">
                        <div className="rp-section-head rp-section-head-row">
                            <div>
                                <h3>Module permissions</h3>
                                <p>
                                    {enabledModuleCount} of {modules.length} modules
                                    enabled
                                </p>
                            </div>
                            <div className="rp-search">
                                <Search size={15} aria-hidden />
                                <input
                                    type="text"
                                    placeholder="Search modules..."
                                    value={search}
                                    onChange={(e) => setSearch(e.target.value)}
                                />
                                {search && (
                                    <button
                                        type="button"
                                        className="rp-search-clear"
                                        aria-label="Clear module search"
                                        onClick={() => setSearch("")}
                                    >
                                        <X size={14} aria-hidden />
                                    </button>
                                )}
                            </div>
                        </div>

                        <div className="rp-matrix">
                            {modulesLoading ? (
                                <div className="rp-state">
                                    <CircularProgress size={26} />
                                </div>
                            ) : visibleModules.length === 0 ? (
                                <div className="rp-state">
                                    {search
                                        ? `No modules match "${search}"`
                                        : "No modules available."}
                                </div>
                            ) : (
                                visibleModules.map((module) => {
                                    const availableKeys = getAvailableKeys(module);
                                    const allChecked = isModuleFullyChecked(module);
                                    const enabled = countEnabled(module);

                                    return (
                                        <div className="rp-module-row" key={module.id}>
                                            <div className="rp-module">
                                                <span
                                                    className={`rp-module-icon${enabled > 0 ? " is-on" : ""
                                                        }`}
                                                    aria-hidden
                                                >
                                                    {(module.moduleName || "M")
                                                        .charAt(0)
                                                        .toUpperCase()}
                                                </span>
                                                <div style={{ minWidth: 0 }}>
                                                    <p className="rp-module-name">
                                                        {module.moduleName}
                                                    </p>
                                                    <p className="rp-module-meta">
                                                        {enabled} of{" "}
                                                        {availableKeys.length} allowed
                                                    </p>
                                                </div>
                                            </div>

                                            <div className="rp-perms">
                                                {renderChip({
                                                    key: `${module.id}-all`,
                                                    label: "All",
                                                    checked: allChecked,
                                                    isAll: true,
                                                    onChange: (e) =>
                                                        handleAllPermissionToggle(
                                                            module.id,
                                                            e.target.checked
                                                        ),
                                                })}

                                                {availableKeys.map((key) =>
                                                    renderChip({
                                                        key: `${module.id}-${key}`,
                                                        label: addSpaceBeforeCapital(
                                                            key.replace("can", "")
                                                        ),
                                                        checked: isChecked(
                                                            module.id,
                                                            key
                                                        ),
                                                        onChange: () =>
                                                            handlePermissionToggle(
                                                                module.id,
                                                                key
                                                            ),
                                                    })
                                                )}
                                            </div>
                                        </div>
                                    );
                                })
                            )}
                        </div>
                    </section>
                </div>

                <div className="rp-footer">
                    <button type="button" className="rp-btn" onClick={handleClose}>
                        {readOnly ? "Close" : "Cancel"}
                    </button>
                    {!readOnly && (
                        <button
                            type="submit"
                            className="rp-btn is-primary"
                            disabled={buttonDisable || loading}
                        >
                            {roleDataById ? "Update Role" : "Add Role"}
                            {loading && (
                                <span
                                    className="spinner-border spinner-border-sm"
                                    role="status"
                                    aria-hidden="true"
                                />
                            )}
                        </button>
                    )}
                </div>
            </form>
        </Dialog>
    );
};

export default AddEditRolesModal;
